"use client";

import { useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { 
  extractLocaleFromPathname, 
  ensureLocaleInUrl,
  DEFAULT_LOCALE 
} from '@/lib/localeUtils';

/**
 * 认证相关的语言处理Hook
 * 专门用于登录、注册等认证流程中的语言处理
 */
export function useAuthLocale() {
  const pathname = usePathname();

  // 获取当前语言
  const getCurrentLocale = useCallback(() => {
    return extractLocaleFromPathname(pathname);
  }, [pathname]);

  // 构建包含语言信息的重定向URL
  const buildAuthRedirectUrl = useCallback((baseUrl, redirectPath, options = {}) => {
    const { 
      preserveLocale = true, 
      targetLocale 
    } = options;
    
    const currentLocale = getCurrentLocale();
    const locale = targetLocale || (preserveLocale ? currentLocale : DEFAULT_LOCALE);
    
    // 确保重定向路径包含正确的语言前缀
    let finalRedirectPath = redirectPath;
    
    if (preserveLocale && locale !== DEFAULT_LOCALE) {
      // 检查路径是否已经包含语言前缀
      if (!redirectPath.startsWith(`/${locale}`)) {
        finalRedirectPath = `/${locale}${redirectPath.startsWith('/') ? redirectPath : '/' + redirectPath}`;
      }
    } else if (locale === DEFAULT_LOCALE) {
      // 如果是默认语言，移除其他语言前缀
      finalRedirectPath = redirectPath.replace(/^\/[a-z]{2}(?=\/|$)/, '') || '/';
    }
    
    const fullUrl = baseUrl + finalRedirectPath;
    
    console.log('[useAuthLocale] Built redirect URL:', {
      currentLocale,
      targetLocale: locale,
      originalPath: redirectPath,
      finalPath: finalRedirectPath,
      fullUrl
    });
    
    return fullUrl;
  }, [getCurrentLocale]);

  // 构建OAuth重定向URL（包含语言信息）
  const buildOAuthRedirectUrl = useCallback((baseUrl, callbackPath, redirectTo, options = {}) => {
    const currentLocale = getCurrentLocale();
    
    // 构建包含语言信息的重定向目标
    const localizedRedirectTo = buildAuthRedirectUrl(baseUrl, redirectTo, {
      targetLocale: currentLocale,
      ...options
    });
    
    // 构建回调URL
    const callbackUrl = `${baseUrl}${callbackPath}`;
    const callbackWithRedirect = `${callbackUrl}?redirectTo=${encodeURIComponent(localizedRedirectTo.replace(baseUrl, ''))}`;
    
    console.log('[useAuthLocale] Built OAuth redirect URL:', {
      currentLocale,
      redirectTo,
      localizedRedirectTo,
      callbackWithRedirect
    });
    
    return callbackWithRedirect;
  }, [getCurrentLocale, buildAuthRedirectUrl]);

  // 准备认证参数（包含语言信息）
  const prepareAuthParams = useCallback((redirectTo, options = {}) => {
    const currentLocale = getCurrentLocale();
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    
    const params = {
      locale: currentLocale,
      redirectUrl: buildAuthRedirectUrl(baseUrl, redirectTo, options),
      ...options
    };
    
    console.log('[useAuthLocale] Prepared auth params:', params);
    
    return params;
  }, [getCurrentLocale, buildAuthRedirectUrl]);

  return {
    getCurrentLocale,
    buildAuthRedirectUrl,
    buildOAuthRedirectUrl,
    prepareAuthParams
  };
} 